// =============================================================================
// VandieZ shipper-web -- Support ticket detail (/support/:ticketId)
// =============================================================================
// Renders the full message thread + a reply box. User can also close
// the ticket once they're satisfied; admin replies arrive in real time
// only when the user refreshes the page (no websocket layer yet).
// =============================================================================

import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Send,
  XCircle,
} from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { supportApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { TicketStatus, TicketWithMessages } from "@/types/support";

const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_TONE: Record<TicketStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  resolved: "bg-success-subtle text-success-subtle-foreground",
  closed: "bg-muted text-muted-foreground",
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function SupportTicketDetailPage(): React.JSX.Element {
  const { accessToken, user } = useAuth();
  const { ticketId } = useParams<{ ticketId: string }>();
  const toast = useToast();
  const [ticket, setTicket] = useState<TicketWithMessages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reply, setReply] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);

  useEffect(() => {
    if (accessToken === null || ticketId === undefined) {return;}
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    supportApi
      .detail(accessToken, ticketId, controller.signal)
      .then((t) => {
        setTicket(t);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        setError(
          err instanceof ApiError ? err.problem.detail : "Couldn't load ticket.",
        );
        setIsLoading(false);
      });
    return () => { controller.abort(); };
  }, [accessToken, ticketId]);

  async function onReply(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (accessToken === null || ticketId === undefined) {return;}
    if (reply.trim().length === 0) {return;}
    setIsSending(true);
    try {
      const updated = await supportApi.reply(accessToken, ticketId, {
        body: reply.trim(),
      });
      setTicket(updated);
      setReply("");
      toast.success("Reply sent.");
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Couldn't send reply.",
      );
    } finally {
      setIsSending(false);
    }
  }

  async function onClose(): Promise<void> {
    if (accessToken === null || ticketId === undefined) {return;}
    // eslint-disable-next-line no-alert
    if (!window.confirm("Close this ticket? You can reopen by replying.")) {return;}
    setIsClosing(true);
    try {
      const updated = await supportApi.close(accessToken, ticketId);
      setTicket(updated);
      toast.success("Ticket closed.");
    } catch (err: unknown) {
      toast.error(
        err instanceof ApiError ? err.problem.detail : "Couldn't close ticket.",
      );
    } finally {
      setIsClosing(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={ticket?.subject ?? "Ticket"}
        description={
          ticket !== null ? (
            <span className="inline-flex items-center gap-2">
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                  STATUS_TONE[ticket.status],
                )}
              >
                {STATUS_LABEL[ticket.status]}
              </span>
              <span className="text-xs uppercase tracking-wider">
                {ticket.category}
              </span>
              <span>· Opened {formatDate(ticket.created_at)}</span>
            </span>
          ) : (
            "Loading…"
          )
        }
        actions={
          <Link to="/support">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4" />
              All tickets
            </Button>
          </Link>
        }
      />

      {error !== null ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {isLoading || ticket === null ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conversation</CardTitle>
              <CardDescription>
                {ticket.message_count} message
                {ticket.message_count === 1 ? "" : "s"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {ticket.messages.map((m) => {
                const fromMe = user !== null && m.sender_user_id === user.id;
                return (
                  <div
                    key={m.id}
                    className={cn(
                      "rounded-md border px-3 py-2",
                      fromMe
                        // Own-message bubble = brand tint, not "success".
                        // /25 keeps the bubble's text-muted-foreground meta
                        // line at 4.54:1; a full-strength primary-subtle fill
                        // drops it to 3.93:1 and fails AA.
                        ? "bg-primary-subtle/25 border-primary"
                        : "bg-background",
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {fromMe ? "You" : m.sender_role}
                      </span>
                      <span>· {formatDate(m.created_at)}</span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">
                      {m.body}
                    </p>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {ticket.status !== "closed" ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Reply</CardTitle>
                <CardDescription>
                  Replies reopen the ticket if it was resolved.
                </CardDescription>
              </CardHeader>
              <form onSubmit={(e) => void onReply(e)}>
                <CardContent>
                  <Textarea
                    rows={4}
                    value={reply}
                    onChange={(e) => { setReply(e.target.value); }}
                    placeholder="Type your reply…"
                  />
                </CardContent>
                <div className="flex flex-wrap items-center justify-end gap-2 p-6 pt-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void onClose()}
                    disabled={isClosing}
                  >
                    {isClosing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    Close ticket
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSending || reply.trim().length === 0}
                  >
                    {isSending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send reply
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </Card>
          ) : (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                This ticket is closed. Open a new ticket if you need
                further help.
              </AlertDescription>
            </Alert>
          )}
        </>
      )}
    </div>
  );
}
